import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import IconBadge from "../components/ui/IconBadge";
import Icon from "../components/ui/Icon";
import Avatar from "../components/ui/Avatar";
import GraficoSaldo from "../components/ui/GraficoSaldo";
import RankingSaldo from "../components/ui/RankingSaldo";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, BarraResumoFiltro } from "../components/ui/ThFiltro";
import { baixarCsv } from "../utils/exportar";
import { formatarHoras, horaLocal } from "../utils/tempo";

const TIPO_AFASTAMENTO_LABEL = { ferias: "Férias", atestado: "Atestado médico", outro: "Outro" };

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const STATUS = {
  falta: { label: "Falta", cor: "#DC2626", bg: "#FEE2E2" },
  completo: { label: "Completo", cor: "#16A34A", bg: "#DCFCE7" },
  completo_banco: { label: "Completo (banco de horas)", cor: "#7C3AED", bg: "#EDE9FE" },
  hora_a_menos: { label: "Horas a menos", cor: "#D97706", bg: "#FEF3C7" },
  batida_incompleta: { label: "Batida incompleta", cor: "#D97706", bg: "#FEF3C7" },
  hora_extra: { label: "Hora extra", cor: "#2563EB", bg: "#DBEAFE" },
  hora_extra_pendente: { label: "Hora extra (aguardando aprovação)", cor: "#D97706", bg: "#FEF3C7" },
  hora_extra_rejeitada: { label: "Hora extra não aprovada", cor: "#6B7280", bg: "#F3F4F6" },
  trabalhado: { label: "Trabalhado", cor: "#16A34A", bg: "#DCFCE7" },
  folga: { label: "Folga", cor: "#6B7280", bg: "#F3F4F6" },
  trabalhou_folga: { label: "Trabalhou na folga", cor: "#7C3AED", bg: "#EDE9FE" },
  feriado: { label: "Feriado", cor: "#0891B2", bg: "#CFFAFE" },
  trabalhou_feriado: { label: "Trabalhou no feriado", cor: "#7C3AED", bg: "#EDE9FE" },
  ferias: { label: "Férias", cor: "#0891B2", bg: "#CFFAFE" },
  atestado: { label: "Atestado médico", cor: "#0891B2", bg: "#CFFAFE" },
  outro: { label: "Afastamento", cor: "#0891B2", bg: "#CFFAFE" },
  atestado_pendente: { label: "Atestado (aguardando aprovação)", cor: "#D97706", bg: "#FEF3C7" },
  sem_registro: { label: "Sem escala definida", cor: "#9CA3AF", bg: "#F9FAFB" },
  em_andamento: { label: "Em andamento", cor: COLORS.primary, bg: COLORS.primaryBg },
  pendente_aprovacao: { label: "Aguardando aprovação", cor: "#D97706", bg: "#FEF3C7" },
  trabalho_previsto: { label: "Trabalho previsto", cor: "#2563EB", bg: "#DBEAFE" },
  futuro: { label: "—", cor: "#D1D5DB", bg: "#FFFFFF" },
  antes_cadastro: { label: "Antes do cadastro", cor: "#D1D5DB", bg: "#FFFFFF" },
};

// Mesma regra usada no backend (hojeLocal em pontoCalculo.js) — nunca usar
// toISOString() pra "hoje", ela converte pra UTC e erra a data à noite (BRT).
function hojeLocalStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}


const DIAS_SEMANA_ABREV = [
  { valor: "dom", label: "D" }, { valor: "seg", label: "S" }, { valor: "ter", label: "T" }, { valor: "qua", label: "Q" },
  { valor: "qui", label: "Q" }, { valor: "sex", label: "S" }, { valor: "sab", label: "S" },
];

// Mesma conversão legada usada em pages/Funcionarios.js — escala_tipo
// 'seg_sex'/'seg_sab' não tem `escala_dias` preenchido (são valores antigos),
// então precisa derivar os dias pra mostrar a pilulazinha certa na tabela.
function diasDoFuncionario(f) {
  if (f.escala_dias) return f.escala_dias.split(",").map((d) => d.trim()).filter(Boolean);
  if (f.escala_tipo === "seg_sex") return ["seg", "ter", "qua", "qui", "sex"];
  if (f.escala_tipo === "seg_sab") return ["seg", "ter", "qua", "qui", "sex", "sab"];
  return [];
}

function formatarHM(hhmmss) {
  return hhmmss ? hhmmss.slice(0, 5) : "—";
}

// Pilulazinha Dom-Sáb só de leitura, usada na tabela de todos os funcionários
// (bem menor que o seletor clicável do cadastro).
function PillsDiasMini({ dias }) {
  if (dias.length === 0) return <span style={{ color: COLORS.textLight, fontSize: 12.5 }}>—</span>;
  return (
    <div style={{ display: "flex", gap: 3 }}>
      {DIAS_SEMANA_ABREV.map((d) => {
        const marcado = dias.includes(d.valor);
        return (
          <span
            key={d.valor}
            title={d.valor}
            style={{
              width: 18, height: 18, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 9.5, fontWeight: 700, background: marcado ? COLORS.primary : COLORS.bg,
              color: marcado ? COLORS.white : COLORS.textLight,
            }}
          >
            {d.label}
          </span>
        );
      })}
    </div>
  );
}

function horaDoTipoTexto(dia, tipo) {
  const r = dia.registros.find((x) => x.tipo === tipo);
  return r ? horaLocal(r.horario) : "";
}

const ORDINAL = ["1ª", "2ª", "3ª", "4ª", "5ª", "6ª"];

// Entrada/saída não podem mais ser lançadas manualmente (pedido do usuário
// 2026-09-17) — sem batida real, a célula sempre mostra "—", sem nenhuma
// ação disponível. Excluir batida também não é mais uma ação daqui (pedido
// do usuário 2026-10-09) — a batida original nunca deve poder sumir direto
// da grade do dia a dia; anular uma batida continua possível só via rota de
// backend dedicada, nunca por um botão solto ao lado de cada horário.
//
// Mostra TODAS as batidas desse tipo no dia, não só a primeira — um toque
// duplo (ex: duas entradas seguidas) vira uma "batida extra" nos cálculos
// (ver calcularSegmentosDoDia), mas antes ficava invisível aqui, só a
// primeira aparecia. Com 2+ do mesmo tipo, numera ("2ª entrada") e destaca
// em laranja a(s) extra(s), pra ficar claro qual é a duplicata.
function HoraExcluivel({ dia, tipo }) {
  const regs = dia.registros.filter((x) => x.tipo === tipo);
  if (!regs.length) {
    return <span style={{ color: COLORS.textLight }}>—</span>;
  }
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
      {regs.map((r, i) => (
        <Link
          key={r.id}
          to={`/comprovante/${r.id}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none",
            color: i > 0 ? COLORS.warning : COLORS.text,
          }}
          title={i > 0 ? `${ORDINAL[i] || `${i + 1}ª`} batida — sobrou sem parear com outra, não deveria ter acontecido` : "Ver comprovante dessa batida"}
        >
          {i > 0 && <span style={{ fontSize: 10, fontWeight: 700 }}>{ORDINAL[i] || `${i + 1}ª`}</span>}
          {horaLocal(r.horario)}
          {i > 0 && <Icon name="alertCircle" size={11} />}
          {r.origem === "manual" && (
            <span title="Lançada manualmente, não foi uma batida pelo app" style={{ display: "inline-flex", color: COLORS.textLight }}>
              <Icon name="edit" size={11} />
            </span>
          )}
          {r.origem === "auto" && (
            <span title="Fechamento automático — a pessoa não bateu, o sistema fechou sozinho 30min depois do horário" style={{ display: "inline-flex", color: COLORS.warning }}>
              <Icon name="alertCircle" size={11} />
            </span>
          )}
          {r.origem_offline && (
            <span title="Batida feita sem internet no celular — sincronizada depois, mas o horário registrado é o do momento real da batida" style={{ display: "inline-flex", color: COLORS.primary }}>
              <Icon name="wifiOff" size={11} />
            </span>
          )}
        </Link>
      ))}
    </span>
  );
}

// Sombra um pouco mais presente que o padrão do Card — dá mais profundidade
// pros cards dessa tela sem exagerar (SHADOW.raised é forte demais, pensada
// pra 1 elemento de destaque só, não pra tela inteira).
function sombraCard(extra) {
  return { boxShadow: "0 3px 12px rgba(16,26,61,0.09), 0 1px 3px rgba(16,26,61,0.06)", border: "none", ...extra };
}

// Resuminho do time — sempre visível na lateral (selecionado ou não alguém),
// pra nunca perder de vista o panorama geral enquanto olha uma pessoa só.
function StatMini({ label, valor, icon, cor }) {
  return (
    <div style={{ background: COLORS.bg, borderRadius: RADIUS.sm, padding: "8px 10px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
        <Icon name={icon} size={12} color={cor} />
        <span style={{ fontSize: 9.5, color: COLORS.textMid, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>{valor}</div>
    </div>
  );
}

export default function HistoricoPonto() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionario, setFuncionario] = useState(null);
  const [ref, setRef] = useState(() => { const d = new Date(); return { ano: d.getFullYear(), mes: d.getMonth() + 1 }; });
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);

  // Um filtro por coluna da tabela do time, digitado ou selecionado direto
  // no cabeçalho — "Setor" reaproveita o campo `equipe` que já existia (só
  // troca o rótulo na tela) e ainda vai pro backend (afeta os cards de
  // resumo/ranking do time); o resto é só client-side, sobre a lista que já
  // veio de `/api/funcionarios` e `fechamentoPorFuncionario`.
  const [filtroSetor, setFiltroSetor] = useState("");
  const [buscaNome, setBuscaNome] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroEscala, setFiltroEscala] = useState("");
  const [filtroEntrada, setFiltroEntrada] = useState("");
  const [filtroAlmoco, setFiltroAlmoco] = useState("");
  const [filtroSaida, setFiltroSaida] = useState("");
  const [filtroAtraso, setFiltroAtraso] = useState("");
  const [filtroFalta, setFiltroFalta] = useState("");

  // Painel do time (quem faltou, quantos com atestado, ranking de saldo, e
  // a própria lista da barra lateral) — reaproveita o mesmo cálculo do
  // Fechamento Mensal, filtrado pelo setor/subsetor escolhido, pro mês em
  // exibição. Uma única fonte de dado pras duas coisas (lista + painel).
  const [fechamento, setFechamento] = useState(null);
  const [loadingFechamento, setLoadingFechamento] = useState(false);

  const [modalAfastamento, setModalAfastamento] = useState(false);
  const [afastamentos, setAfastamentos] = useState([]);
  const [formAfastamento, setFormAfastamento] = useState({ data_inicio: "", data_fim: "", tipo: "ferias", observacao: "" });
  const [salvandoAfastamento, setSalvandoAfastamento] = useState(false);
  const [erroAfastamento, setErroAfastamento] = useState("");

  useEffect(() => {
    apiFetch("GET", "/api/funcionarios", undefined, token).then((lista) => {
      setFuncionarios(lista);
      // Link vindo do Dashboard (pendência de batida incompleta) já chega
      // sabendo qual funcionário abrir — sem isso, o gestor teria que
      // procurar de novo o mesmo funcionário que acabou de ser apontado.
      // Sem esse link, a tela abre sem ninguém selecionado de propósito —
      // só a visão do time, até o gestor escolher alguém explicitamente.
      const idAlvo = searchParams.get("funcionario_id");
      const alvo = idAlvo && lista.find((f) => String(f.id) === idAlvo);
      if (alvo) setFuncionario(alvo);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Guarda qual foi a última chamada — sem isso, trocar de mês rápido duas
  // vezes podia fazer a resposta mais lenta (do mês anterior) chegar depois
  // e sobrescrever o mês certo na tela (achado numa varredura de bugs
  // 2026-10-25). Só aplica o resultado se ainda for a chamada mais recente.
  const carregarIdRef = useRef(0);
  const carregar = useCallback(async () => {
    if (!funcionario) return;
    const idDaChamada = ++carregarIdRef.current;
    setLoading(true);
    try {
      const data = await apiFetch("GET", `/api/relatorios/espelho-ponto?funcionario_id=${funcionario.id}&ano=${ref.ano}&mes=${ref.mes}`, undefined, token);
      if (idDaChamada === carregarIdRef.current) setDados(data);
    } finally {
      if (idDaChamada === carregarIdRef.current) setLoading(false);
    }
  }, [funcionario, ref, token]);

  useEffect(() => { carregar(); }, [carregar]);

  const carregarFechamento = useCallback(async () => {
    setLoadingFechamento(true);
    try {
      const query = new URLSearchParams({ ano: ref.ano, mes: ref.mes });
      if (filtroSetor) query.set("equipe", filtroSetor);
      const data = await apiFetch("GET", `/api/relatorios/fechamento-mensal?${query}`, undefined, token);
      setFechamento(data);
    } finally {
      setLoadingFechamento(false);
    }
  }, [ref, filtroSetor, token]);

  useEffect(() => { carregarFechamento(); }, [carregarFechamento]);

  // Aceita um funcionário explícito (usado pelo painel do time, que pode
  // querer abrir afastamento de alguém diferente de quem está selecionado no
  // momento) em vez de sempre ler o estado `funcionario` — que só atualiza no
  // próximo render, tarde demais pra usar aqui dentro da mesma chamada.
  const carregarAfastamentos = useCallback(async (funcionarioId) => {
    if (!funcionarioId) return;
    const data = await apiFetch("GET", `/api/afastamentos?funcionario_id=${funcionarioId}`, undefined, token);
    setAfastamentos(data);
  }, [token]);

  const abrirAfastamento = (alvo, tipoInicial) => {
    const alvoFinal = alvo || funcionario;
    if (!alvoFinal) return;
    if (alvo) setFuncionario(alvo);
    setFormAfastamento({ data_inicio: "", data_fim: "", tipo: tipoInicial || "ferias", observacao: "" });
    setErroAfastamento("");
    setModalAfastamento(true);
    carregarAfastamentos(alvoFinal.id);
  };

  const salvarAfastamento = async () => {
    setErroAfastamento("");
    if (!formAfastamento.data_inicio || !formAfastamento.data_fim) {
      setErroAfastamento("Preencha a data de início e fim.");
      return;
    }
    setSalvandoAfastamento(true);
    try {
      await apiFetch("POST", "/api/afastamentos", { funcionario_id: funcionario.id, ...formAfastamento }, token);
      setFormAfastamento({ data_inicio: "", data_fim: "", tipo: "ferias", observacao: "" });
      await carregarAfastamentos(funcionario.id);
      await carregar();
      await carregarFechamento();
    } catch (e) {
      setErroAfastamento(e.message);
    } finally {
      setSalvandoAfastamento(false);
    }
  };

  const excluirAfastamento = async (afastamento) => {
    if (!window.confirm(`Remover esse afastamento (${afastamento.data_inicio} a ${afastamento.data_fim})?`)) return;
    try {
      await apiFetch("DELETE", `/api/afastamentos/${afastamento.id}`, undefined, token);
      await carregarAfastamentos(funcionario.id);
      await carregar();
      await carregarFechamento();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const mudarMes = (delta) => {
    let { ano, mes } = ref;
    mes += delta;
    if (mes > 12) { mes = 1; ano += 1; }
    if (mes < 1) { mes = 12; ano -= 1; }
    setRef({ ano, mes });
  };

  const exportarCsv = () => {
    if (!dados || !funcionario) return;
    const cabecalhos = ["Dia", "Entrada", "Intervalo início", "Intervalo fim", "Saída", "Horas trabalhadas", "Banco de horas", "Saldo do dia", "Saldo acumulado no mês", "Status"];
    const linhas = dados.dias
      .filter((dia) => dia.status !== "futuro")
      .map((dia) => [
        `${DIAS_SEMANA[dia.dia_semana]} ${dia.data.split("-").reverse().join("/")}`,
        horaDoTipoTexto(dia, "entrada"),
        horaDoTipoTexto(dia, "intervalo_inicio"),
        horaDoTipoTexto(dia, "intervalo_fim"),
        horaDoTipoTexto(dia, "saida"),
        dia.registros.length > 0 ? formatarHoras(dia.horas_trabalhadas) : "",
        dia.horas_banco > 0 ? formatarHoras(dia.horas_banco) : "",
        dia.saldo_dia === null ? "" : formatarHoras(dia.saldo_dia),
        dia.saldo_acumulado === null ? "" : formatarHoras(dia.saldo_acumulado),
        STATUS[dia.status]?.label || "",
      ]);
    const nomeArquivo = `${funcionario.nome.replace(/[^\w\-]+/g, "_")}-${String(ref.mes).padStart(2, "0")}-${ref.ano}.csv`;
    baixarCsv(nomeArquivo, cabecalhos, linhas);
  };

  // Último dia do mês que já tem saldo acumulado calculado (dias futuros ficam
  // null) — é o "saldo do mês até agora", mostrado separado da tabela dia a dia
  // pra não parecer resultado de um dia só.
  const saldoMensal = (() => {
    const dias = dados?.dias || [];
    for (let i = dias.length - 1; i >= 0; i--) {
      if (dias[i].saldo_acumulado !== null) return dias[i].saldo_acumulado;
    }
    return null;
  })();

  // Lookup pras colunas de Atrasos/Faltas da tabela principal e pros cards
  // do funcionário selecionado — mesmo dado do fechamento mensal, só
  // reindexado por funcionario_id pra achar rápido.
  const fechamentoPorFuncionario = {};
  for (const f of fechamento?.funcionarios || []) fechamentoPorFuncionario[f.funcionario_id] = f;

  const resumoMesSelecionado = funcionario ? fechamentoPorFuncionario[funcionario.id] : null;
  const totalFaltasMes = resumoMesSelecionado?.dias_falta ?? 0;
  const totalAtrasosMes = resumoMesSelecionado?.atrasos_qtd ?? 0;

  const setoresDisponiveis = [...new Set(funcionarios.map((f) => f.equipe).filter(Boolean))].sort();
  const empresasDisponiveis = [...new Set(funcionarios.map((f) => f.empresa_nome).filter(Boolean))].sort();

  // Tabela principal (substitui a antiga lista lateral) — vem direto de
  // `funcionarios` (cadastro completo, com empresa/escala), não do
  // fechamento mensal, que só tem os números de falta/saldo. Atraso/Falta
  // filtram sobre o mesmo `fechamentoPorFuncionario` já usado nas colunas.
  const funcionariosTabela = funcionarios.filter((f) => {
    if (!f.ativo) return false;
    if (filtroSetor && f.equipe !== filtroSetor) return false;
    if (buscaNome && !f.nome.toLowerCase().includes(buscaNome.toLowerCase())) return false;
    if (filtroEmpresa && f.empresa_nome !== filtroEmpresa) return false;
    if (filtroEscala === "12x36" && f.escala_tipo !== "12x36") return false;
    if (filtroEscala === "normal" && f.escala_tipo === "12x36") return false;
    if (filtroEntrada && !formatarHM(f.horario_entrada).toLowerCase().includes(filtroEntrada.toLowerCase())) return false;
    if (filtroSaida && !formatarHM(f.horario_saida).toLowerCase().includes(filtroSaida.toLowerCase())) return false;
    if (filtroAlmoco) {
      const almocoTexto = f.almoco_inicio && f.almoco_fim ? `${formatarHM(f.almoco_inicio)}–${formatarHM(f.almoco_fim)}` : "—";
      if (!almocoTexto.toLowerCase().includes(filtroAlmoco.toLowerCase())) return false;
    }
    const resumoMes = fechamentoPorFuncionario[f.id];
    if (filtroAtraso === "com" && !(resumoMes?.atrasos_qtd > 0)) return false;
    if (filtroAtraso === "sem" && resumoMes?.atrasos_qtd > 0) return false;
    if (filtroFalta === "com" && !(resumoMes?.dias_falta > 0)) return false;
    if (filtroFalta === "sem" && resumoMes?.dias_falta > 0) return false;
    return true;
  });

  const filtrosTabelaAtivos = Boolean(
    buscaNome || filtroSetor || filtroEmpresa || filtroEscala || filtroEntrada || filtroAlmoco || filtroSaida || filtroAtraso || filtroFalta
  );
  const limparFiltrosTabela = () => {
    setBuscaNome(""); setFiltroSetor(""); setFiltroEmpresa(""); setFiltroEscala("");
    setFiltroEntrada(""); setFiltroAlmoco(""); setFiltroSaida(""); setFiltroAtraso(""); setFiltroFalta("");
  };

  // Só entra na lista de atenção quem tem falta sem atestado ou dias de
  // atestado esse mês — sem issue nenhum não aparece, pra lista ficar curta
  // e focada em quem o gestor realmente precisa olhar.
  const precisamAtencao = (fechamento?.funcionarios || []).filter((f) => f.dias_falta > 0 || f.dias_atestado > 0);
  const totalBateram = (fechamento?.funcionarios || []).filter((f) => f.dias_trabalhados > 0).length;
  const totalFuncionariosTime = fechamento?.funcionarios?.length || 0;
  const totalFaltasSemAtestado = (fechamento?.funcionarios || []).reduce((soma, f) => soma + f.dias_falta, 0);
  const totalDiasAtestado = (fechamento?.funcionarios || []).reduce((soma, f) => soma + f.dias_atestado, 0);
  const saldoLiquidoTime = (fechamento?.total_saldos_positivos || 0) - (fechamento?.total_saldos_negativos || 0);

  const verDetalheDoTime = (funcionarioId) => {
    const alvo = funcionarios.find((f) => f.id === funcionarioId);
    if (alvo) setFuncionario(alvo);
  };

  const lancarAtestadoDoTime = (funcionarioId) => {
    const alvo = funcionarios.find((f) => f.id === funcionarioId);
    if (alvo) abrirAfastamento(alvo, "atestado");
  };

  const hojeStr = hojeLocalStr();

  return (
    <div>
      <div style={{ marginBottom: 24, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Folha de Ponto
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            {funcionario ? `${funcionario.nome} — ${MESES[ref.mes - 1]} de ${ref.ano}` : "Clique em um funcionário na lista pra ver as batidas, faltas e horas dia a dia."}
          </p>
        </div>

        <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 10, background: COLORS.primaryBg, border: `1.5px solid ${COLORS.primary}33`, borderRadius: RADIUS.md, padding: "6px 10px" }}>
          <button onClick={() => mudarMes(-1)} style={{ ...navBtn(), color: COLORS.primary }}>‹</button>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.primary, minWidth: 130, textAlign: "center" }}>
            {MESES[ref.mes - 1]} {ref.ano}
          </span>
          <button onClick={() => mudarMes(1)} style={{ ...navBtn(), color: COLORS.primary }}>›</button>
        </div>
      </div>

      <div>
        {funcionario ? (
            <>
              <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                <button
                  onClick={() => setFuncionario(null)}
                  title="Voltar pra visão do time"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7, flexShrink: 0,
                    background: COLORS.surface, border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md,
                    padding: "9px 14px 9px 11px", fontSize: 13, fontWeight: 700, color: COLORS.primary, cursor: "pointer",
                    boxShadow: "0 1px 3px rgba(16,26,61,0.08)",
                  }}
                >
                  <Icon name="arrowLeft" size={15} color={COLORS.primary} />
                  Time
                </button>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: COLORS.text, margin: 0, flex: 1, minWidth: 0 }}>{funcionario.nome}</h2>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Btn variant="secondary" onClick={exportarCsv} disabled={!dados} style={{ padding: "8px 14px", fontSize: 12.5 }}>Baixar CSV</Btn>
                  <Btn variant="secondary" onClick={() => window.print()} disabled={!dados} style={{ padding: "8px 14px", fontSize: 12.5 }}>Imprimir / PDF</Btn>
                  <Btn variant="secondary" onClick={() => abrirAfastamento()} style={{ padding: "8px 14px", fontSize: 12.5 }}>Lançar Afastamento</Btn>
                </div>
              </div>

              {dados && (
                <div className="no-print" style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", maxWidth: 640 }}>
                  <Card style={sombraCard({ flex: "1 1 260px", padding: 14 })}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 2 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <IconBadge icon="barChart" size={22} cor={COLORS.primary} />
                        <p style={{ fontSize: 10.5, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em", margin: 0 }}>
                          Saldo — {funcionario?.nome?.split(" ")[0]}
                        </p>
                      </div>
                      {saldoMensal !== null && (
                        <p style={{ fontSize: 16, fontWeight: 800, margin: 0, fontVariantNumeric: "tabular-nums", color: saldoMensal > 0 ? COLORS.success : saldoMensal < 0 ? COLORS.danger : COLORS.textMid }}>
                          {formatarHoras(saldoMensal)}
                        </p>
                      )}
                    </div>
                    <GraficoSaldo dias={dados.dias} />
                  </Card>

                  <Card style={sombraCard({ flex: "1 1 120px", padding: 14, display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 })}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <IconBadge icon="alertCircle" size={22} cor={COLORS.danger} />
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Faltas no mês
                      </span>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: totalFaltasMes > 0 ? COLORS.danger : COLORS.text }}>
                      {totalFaltasMes}
                    </div>
                  </Card>

                  <Card style={sombraCard({ flex: "1 1 120px", padding: 14, display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 })}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <IconBadge icon="clock" size={22} cor={COLORS.warning} />
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Atrasos no mês
                      </span>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 800, fontVariantNumeric: "tabular-nums", color: totalAtrasosMes > 0 ? COLORS.danger : COLORS.text }}>
                      {totalAtrasosMes}
                    </div>
                  </Card>
                </div>
              )}

              <Card className="tabela-mes-print" style={sombraCard({ padding: 0 })}>
                {loading || !dados ? (
                  <EstadoCarregando />
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                          {["Dia", "Entrada", "Intervalo", "Saída", "Horas", "Banco de horas", "Saldo do dia", "Status"].map((h) => (
                            <th key={h} style={{ textAlign: "left", padding: "12px 16px", fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dados.dias.map((dia) => {
                          const futuro = dia.eh_futuro;
                          const ehHoje = dia.data === hojeStr;
                          const ehFimDeSemana = dia.dia_semana === 0 || dia.dia_semana === 6;
                          // Em dias futuros a escala já é conhecida (segunda-sexta/segunda-sábado
                          // dão pra prever), mesmo sem saber ainda se a pessoa vai bater ponto —
                          // 12x36 continua sem previsão por falta de data de referência no cadastro.
                          // Se o dia futuro já tem um afastamento aprovado/pendente cobrindo ele,
                          // `status` deixa de ser "futuro" (ver pontoCalculo.js) e cai direto no
                          // branch de baixo, mostrando o status real em vez do genérico.
                          const cfg = dia.status === "futuro"
                            ? (dia.esperado === true ? STATUS.trabalho_previsto : dia.esperado === false ? STATUS.folga : null)
                            : STATUS[dia.status] || STATUS.sem_registro;
                          const temIntervalo = dia.registros.some((r) => r.tipo === "intervalo_inicio" || r.tipo === "intervalo_fim");
                          return (
                            <tr
                              key={dia.data}
                              style={{
                                borderBottom: `1px solid ${COLORS.border}`,
                                opacity: futuro || dia.status === "antes_cadastro" ? 0.65 : 1,
                                background: ehHoje ? COLORS.primaryBg : ehFimDeSemana ? COLORS.bg : "transparent",
                              }}
                            >
                              <td style={{ padding: "10px 16px", fontSize: 13, whiteSpace: "nowrap", borderLeft: ehHoje ? `3px solid ${COLORS.primary}` : "3px solid transparent" }}>
                                <span style={{ fontWeight: 700, color: COLORS.text }}>{DIAS_SEMANA[dia.dia_semana]}</span>{" "}
                                <span style={{ color: COLORS.textMid }}>{dia.data.slice(-2)}/{dia.data.slice(5, 7)}</span>
                                {ehHoje && (
                                  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: COLORS.primary, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                    hoje
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text, whiteSpace: "nowrap" }}>
                                <HoraExcluivel dia={dia} tipo="entrada" />
                              </td>
                              <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text, whiteSpace: "nowrap" }}>
                                {temIntervalo ? (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    <HoraExcluivel dia={dia} tipo="intervalo_inicio" />
                                    <span style={{ color: COLORS.textLight }}>–</span>
                                    <HoraExcluivel dia={dia} tipo="intervalo_fim" />
                                  </span>
                                ) : "—"}
                              </td>
                              <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text, whiteSpace: "nowrap" }}>
                                <HoraExcluivel dia={dia} tipo="saida" />
                              </td>
                              <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>
                                {dia.registros.length > 0 ? formatarHoras(dia.horas_trabalhadas) : "—"}
                              </td>
                              <td style={{ padding: "10px 16px", fontSize: 13, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>
                                {dia.horas_banco > 0 ? formatarHoras(dia.horas_banco) : "—"}
                              </td>
                              <td style={{ padding: "10px 16px", fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                {dia.saldo_dia === null ? (
                                  <span style={{ color: COLORS.textLight, fontWeight: 400 }}>—</span>
                                ) : (
                                  <span style={{ color: dia.saldo_dia > 0 ? COLORS.success : dia.saldo_dia < 0 ? COLORS.danger : COLORS.textMid }}>
                                    {formatarHoras(dia.saldo_dia)}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "10px 16px" }}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  {cfg && (
                                    <span
                                      style={{
                                        display: "inline-flex", alignItems: "center", gap: 6, background: cfg.bg, color: cfg.cor,
                                        borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                                      }}
                                    >
                                      {cfg.label}
                                    </span>
                                  )}
                                  {dia.tem_batida_extra && (
                                    <span
                                      title="Tem batida sobrando fora de um turno válido (ex: duas entradas seguidas sem saída entre elas) — não entrou na conta de horas. Confira os horários batidos nesse dia."
                                      style={{
                                        display: "inline-flex", alignItems: "center", gap: 4, background: "#FEF3C7", color: "#92600A",
                                        borderRadius: 6, padding: "3px 8px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", cursor: "help",
                                      }}
                                    >
                                      ⚠ batida extra
                                    </span>
                                  )}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </>
          ) : (
            <div>
              {fechamento && (
                <div className="no-print" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
                  <StatMini label="Bateram" valor={`${totalBateram}/${totalFuncionariosTime}`} icon="checkCircle" cor={COLORS.success} />
                  <StatMini label="Faltas" valor={totalFaltasSemAtestado} icon="alertCircle" cor={COLORS.danger} />
                  <StatMini label="Atestados" valor={totalDiasAtestado} icon="heart" cor={COLORS.info} />
                  <StatMini
                    label="Saldo time"
                    valor={formatarHoras(saldoLiquidoTime)}
                    icon="clock"
                    cor={saldoLiquidoTime > 0 ? COLORS.success : saldoLiquidoTime < 0 ? COLORS.danger : COLORS.primary}
                  />
                </div>
              )}

              {/* Tabela com todos os funcionários (mesmo estilo da tela de
                  Cadastro): nome, empresa, setor e a escala de trabalho
                  cadastrada — clicar numa linha abre a folha de ponto mensal
                  dela, com as batidas reais de cada dia. Cada coluna tem seu
                  próprio filtro (digitável ou selecionável) embutido no
                  cabeçalho, em vez de uma barra de filtros separada. */}
              <BarraResumoFiltro
                total={funcionariosTabela.length}
                totalGeral={funcionarios.filter((f) => f.ativo).length}
                unidade="funcionário(s)"
                filtrosAtivos={filtrosTabelaAtivos}
                onLimpar={limparFiltrosTabela}
              />

              <Card style={sombraCard({ padding: 0, marginBottom: 20 })}>
                {funcionariosTabela.length === 0 ? (
                  <EstadoVazio icon="users" texto={filtrosTabelaAtivos ? "Nenhum funcionário encontrado com esse filtro." : "Nenhum funcionário ativo ainda."} />
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: `1.5px solid ${COLORS.border}` }}>
                          <ThFiltroTexto label="Nome" value={buscaNome} onChange={setBuscaNome} placeholder="Buscar nome..." minWidth={170} />
                          <ThFiltroSelect
                            label="Empresa" value={filtroEmpresa} onChange={setFiltroEmpresa} minWidth={150}
                            opcoes={[{ value: "", label: "Todas" }, ...empresasDisponiveis.map((e) => ({ value: e, label: e }))]}
                          />
                          <ThFiltroSelect
                            label="Setor" value={filtroSetor} onChange={setFiltroSetor} minWidth={150}
                            opcoes={[{ value: "", label: "Todos" }, ...setoresDisponiveis.map((s) => ({ value: s, label: s }))]}
                          />
                          <ThFiltroSelect
                            label="Dias de trabalho" value={filtroEscala} onChange={setFiltroEscala} minWidth={140}
                            opcoes={[{ value: "", label: "Todos" }, { value: "12x36", label: "12x36" }, { value: "normal", label: "Escala normal" }]}
                          />
                          <ThFiltroTexto label="Entrada" value={filtroEntrada} onChange={setFiltroEntrada} placeholder="Ex: 08:00" minWidth={100} />
                          <ThFiltroTexto label="Almoço" value={filtroAlmoco} onChange={setFiltroAlmoco} placeholder="Ex: 12:00" minWidth={110} />
                          <ThFiltroTexto label="Saída" value={filtroSaida} onChange={setFiltroSaida} placeholder="Ex: 18:00" minWidth={100} />
                          <ThFiltroSelect
                            label="Atrasos" value={filtroAtraso} onChange={setFiltroAtraso} minWidth={110}
                            opcoes={[{ value: "", label: "Todos" }, { value: "com", label: "Com atraso" }, { value: "sem", label: "Sem atraso" }]}
                          />
                          <ThFiltroSelect
                            label="Faltas" value={filtroFalta} onChange={setFiltroFalta} minWidth={110}
                            opcoes={[{ value: "", label: "Todos" }, { value: "com", label: "Com falta" }, { value: "sem", label: "Sem falta" }]}
                          />
                        </tr>
                      </thead>
                      <tbody>
                        {funcionariosTabela.map((f) => {
                          const resumoMes = fechamentoPorFuncionario[f.id];
                          return (
                          <tr
                            key={f.id}
                            onClick={() => setFuncionario(f)}
                            style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer" }}
                          >
                            <td style={{ padding: "10px 14px", fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{f.nome}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid }}>{f.empresa_nome || "—"}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid }}>{f.setor || f.equipe || "—"}</td>
                            <td style={{ padding: "10px 14px" }}>
                              {f.escala_tipo === "12x36" ? (
                                <span style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.primary, background: COLORS.primaryBg, borderRadius: 6, padding: "3px 9px" }}>
                                  12x36
                                </span>
                              ) : (
                                <PillsDiasMini dias={diasDoFuncionario(f)} />
                              )}
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>{formatarHM(f.horario_entrada)}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>
                              {f.almoco_inicio && f.almoco_fim ? `${formatarHM(f.almoco_inicio)}–${formatarHM(f.almoco_fim)}` : "—"}
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: COLORS.textMid, whiteSpace: "nowrap" }}>{formatarHM(f.horario_saida)}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13.5, fontWeight: resumoMes?.atrasos_qtd > 0 ? 700 : 400, color: resumoMes?.atrasos_qtd > 0 ? COLORS.danger : COLORS.textMid }}>
                              {resumoMes ? resumoMes.atrasos_qtd : "—"}
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 13.5, fontWeight: resumoMes?.dias_falta > 0 ? 700 : 400, color: resumoMes?.dias_falta > 0 ? COLORS.danger : COLORS.textMid }}>
                              {resumoMes ? resumoMes.dias_falta : "—"}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {loadingFechamento || !fechamento ? null : (
                <>
                  {(fechamento.maiores_saldos.length > 0 || fechamento.menores_saldos.length > 0) && (
                    <div style={{ marginBottom: 16 }}>
                      <RankingSaldo maiores={fechamento.maiores_saldos} menores={fechamento.menores_saldos} />
                    </div>
                  )}

                  {precisamAtencao.length > 0 && (
                    <Card style={sombraCard({ padding: 0 })}>
                      <div style={{ padding: "14px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                        <IconBadge icon="alertCircle" size={28} cor={COLORS.warning} />
                        <div>
                          <h3 style={{ fontSize: 13.5, fontWeight: 800, color: COLORS.text, margin: 0 }}>Quem precisa de atenção</h3>
                          <p style={{ fontSize: 11.5, color: COLORS.textMid, margin: "3px 0 0" }}>
                            Faltas sem atestado (sujeitas a desconto) ou dias com atestado esse mês.
                          </p>
                        </div>
                      </div>
                      {precisamAtencao.map((f, i) => (
                        <div
                          key={f.funcionario_id}
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
                            padding: "12px 20px", borderBottom: i < precisamAtencao.length - 1 ? `1px solid ${COLORS.border}` : "none",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Avatar nome={f.nome} size={28} />
                            <div>
                              <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{f.nome}</div>
                              {(f.cargo || f.equipe) && (
                                <div style={{ fontSize: 11.5, color: COLORS.textLight }}>{[f.cargo, f.equipe].filter(Boolean).join(" · ")}</div>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                            {f.dias_falta > 0 && (
                              <span style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.danger, background: COLORS.dangerBg, borderRadius: 6, padding: "3px 9px" }}>
                                {f.dias_falta} falta{f.dias_falta > 1 ? "s" : ""} sem atestado — sujeita a desconto
                              </span>
                            )}
                            {f.dias_atestado > 0 && (
                              <span style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.info, background: COLORS.infoBg, borderRadius: 6, padding: "3px 9px" }}>
                                {f.dias_atestado} dia{f.dias_atestado > 1 ? "s" : ""} com atestado
                              </span>
                            )}
                            <button onClick={() => verDetalheDoTime(f.funcionario_id)} style={linkBtnStyle()}>Ver detalhe</button>
                            {f.dias_falta > 0 && (
                              <button onClick={() => lancarAtestadoDoTime(f.funcionario_id)} style={linkBtnStyle()}>Lançar atestado</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </Card>
                  )}
                </>
              )}
            </div>
          )}
      </div>

      <Modal open={modalAfastamento} onClose={() => setModalAfastamento(false)} title={`Afastamentos — ${funcionario?.nome || ""}`} width={460}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <Input
                label="Início"
                type="date"
                value={formAfastamento.data_inicio}
                onChange={(e) => setFormAfastamento({ ...formAfastamento, data_inicio: e.target.value })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                label="Fim"
                type="date"
                value={formAfastamento.data_fim}
                onChange={(e) => setFormAfastamento({ ...formAfastamento, data_fim: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 }}>Tipo</label>
            <select
              value={formAfastamento.tipo}
              onChange={(e) => setFormAfastamento({ ...formAfastamento, tipo: e.target.value })}
              style={{ width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "11px 14px", fontSize: 14, background: COLORS.bg, color: COLORS.text }}
            >
              <option value="ferias">Férias</option>
              <option value="atestado">Atestado médico</option>
              <option value="outro">Outro</option>
            </select>
          </div>
          <Input
            label="Observação (opcional)"
            value={formAfastamento.observacao}
            onChange={(e) => setFormAfastamento({ ...formAfastamento, observacao: e.target.value })}
            placeholder="Ex: cirurgia, viagem, licença..."
          />
          {erroAfastamento && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erroAfastamento}
            </div>
          )}
          <Btn onClick={salvarAfastamento} disabled={salvandoAfastamento}>
            {salvandoAfastamento ? "Salvando..." : "Registrar afastamento"}
          </Btn>

          {afastamentos.length > 0 && (
            <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 4, paddingTop: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <IconBadge icon="heart" size={28} cor={COLORS.primary} />
                <p style={{ fontSize: 12, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.04em", margin: 0 }}>
                  Já registrados
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 200, overflowY: "auto" }}>
                {afastamentos.map((a) => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12.5, background: COLORS.bg, borderRadius: RADIUS.sm, padding: "8px 10px" }}>
                    <span style={{ color: COLORS.text }}>
                      <strong>{TIPO_AFASTAMENTO_LABEL[a.tipo]}</strong> — {a.data_inicio.split("-").reverse().join("/")} a {a.data_fim.split("-").reverse().join("/")}
                    </span>
                    <button
                      onClick={() => excluirAfastamento(a)}
                      style={{ background: "transparent", border: "none", color: COLORS.danger, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function navBtn() {
  return { background: "transparent", border: "none", fontSize: 18, color: COLORS.textMid, cursor: "pointer", padding: "0 4px", lineHeight: 1 };
}

function linkBtnStyle() {
  return { background: "none", border: "none", color: COLORS.primary, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline" };
}
